import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Check,
  X,
  Save,
  Eye,
  Sparkles,
  Clock,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { v4 as uuidv4 } from 'uuid';

const subjects = [
  { id: 'matematica', name: 'Matemática' },
  { id: 'portugues', name: 'Português' },
  { id: 'ciencias', name: 'Ciências' },
  { id: 'historia', name: 'História' },
  { id: 'geografia', name: 'Geografia' },
  { id: 'ingles', name: 'Inglês' },
  { id: 'artes', name: 'Artes' },
  { id: 'educacao_fisica', name: 'Educação Física' }
];

const grades = [
  { id: '1ano_ef', name: '1º Ano EF' },
  { id: '2ano_ef', name: '2º Ano EF' },
  { id: '3ano_ef', name: '3º Ano EF' },
  { id: '4ano_ef', name: '4º Ano EF' },
  { id: '5ano_ef', name: '5º Ano EF' },
  { id: '6ano_ef', name: '6º Ano EF' },
  { id: '7ano_ef', name: '7º Ano EF' },
  { id: '8ano_ef', name: '8º Ano EF' },
  { id: '9ano_ef', name: '9º Ano EF' },
  { id: '1ano_em', name: '1º Ano EM' },
  { id: '2ano_em', name: '2º Ano EM' },
  { id: '3ano_em', name: '3º Ano EM' }
];

const difficulties = [
  { id: 'easy', name: 'Fácil', color: 'bg-green-500' },
  { id: 'medium', name: 'Médio', color: 'bg-amber-500' },
  { id: 'hard', name: 'Difícil', color: 'bg-red-500' }
];

const createEmptyQuestion = () => ({
  id: uuidv4(),
  question: '',
  question_type: 'multiple_choice',
  options: [
    { id: uuidv4(), text: '', is_correct: true },
    { id: uuidv4(), text: '', is_correct: false },
    { id: uuidv4(), text: '', is_correct: false },
    { id: uuidv4(), text: '', is_correct: false }
  ],
  explanation: '',
  difficulty: 'medium',
  time_limit: 30,
  points: 100
});

export default function QuizLiveCreate() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  
  const [quizData, setQuizData] = useState({
    title: '',
    description: '',
    subject: '',
    grade_level: '',
    is_public: false
  });

  const [questions, setQuestions] = useState([createEmptyQuestion()]);
  const [activeQuestion, setActiveQuestion] = useState(0);

  const handleQuizChange = (field, value) => {
    setQuizData(prev => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleOptionChange = (questionIndex, optionIndex, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      const options = [...updated[questionIndex].options];
      
      if (field === 'is_correct' && value === true) {
        // Only one option can be correct
        options.forEach((opt, i) => {
          options[i] = { ...opt, is_correct: i === optionIndex };
        });
      } else {
        options[optionIndex] = { ...options[optionIndex], [field]: value };
      }
      
      updated[questionIndex] = { ...updated[questionIndex], options };
      return updated;
    });
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, createEmptyQuestion()]);
    setActiveQuestion(questions.length);
  };

  const removeQuestion = (index) => {
    if (questions.length <= 1) {
      toast.error('O quiz precisa ter pelo menos uma questão');
      return;
    }
    
    setQuestions(prev => prev.filter((_, i) => i !== index));
    if (activeQuestion >= index && activeQuestion > 0) {
      setActiveQuestion(activeQuestion - 1);
    }
  };

  const addOption = (questionIndex) => {
    setQuestions(prev => {
      const updated = [...prev];
      updated[questionIndex].options.push({
        id: uuidv4(),
        text: '',
        is_correct: false
      });
      return updated;
    });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const question = questions[questionIndex];
    if (question.options.length <= 2) {
      toast.error('A questão precisa ter pelo menos 2 opções');
      return;
    }
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
      
      // If removed option was correct, make first option correct
      if (!updated[questionIndex].options.some(o => o.is_correct)) {
        updated[questionIndex].options[0].is_correct = true;
      }
      
      return updated;
    });
  };

  const validateQuiz = () => {
    if (!quizData.title.trim()) {
      toast.error('Digite um título para o quiz');
      return false;
    }
    if (!quizData.subject) {
      toast.error('Selecione uma disciplina');
      return false;
    }
    if (!quizData.grade_level) {
      toast.error('Selecione uma série');
      return false;
    }
    
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.question.trim()) {
        toast.error(`Questão ${i + 1}: Digite o enunciado`);
        setActiveQuestion(i);
        return false;
      }
      
      const filledOptions = q.options.filter(o => o.text.trim());
      if (filledOptions.length < 2) {
        toast.error(`Questão ${i + 1}: Preencha pelo menos 2 alternativas`);
        setActiveQuestion(i);
        return false;
      }
      
      if (!q.options.some(o => o.is_correct && o.text.trim())) {
        toast.error(`Questão ${i + 1}: Marque uma alternativa como correta`);
        setActiveQuestion(i);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateQuiz()) return;
    
    setSaving(true);
    try {
      // Filter out empty options
      const cleanQuestions = questions.map(q => ({
        ...q,
        options: q.options.filter(o => o.text.trim())
      }));
      
      await api.post('/quiz-live/quizzes', {
        ...quizData,
        questions: cleanQuestions
      });
      
      toast.success('Quiz criado com sucesso!');
      navigate('/quiz-live');
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Erro ao salvar quiz');
    } finally {
      setSaving(false);
    }
  };

  const currentQuestion = questions[activeQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/quiz-live')}
              className="text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Criar Quiz</h1>
              <p className="text-sm text-gray-400">{questions.length} questões</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-white/20 text-white">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-purple-600 to-green-500"
              data-testid="save-quiz-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Quiz'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Quiz Info & Question List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Quiz Info */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Informações</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300 text-sm">Título *</Label>
                  <Input
                    value={quizData.title}
                    onChange={(e) => handleQuizChange('title', e.target.value)}
                    placeholder="Ex: Quiz de Matemática"
                    className="mt-1 bg-white/10 border-white/20 text-white"
                    data-testid="quiz-title-input"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300 text-sm">Disciplina *</Label>
                  <Select
                    value={quizData.subject}
                    onValueChange={(v) => handleQuizChange('subject', v)}
                  >
                    <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white" data-testid="quiz-subject-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300 text-sm">Série *</Label>
                  <Select
                    value={quizData.grade_level}
                    onValueChange={(v) => handleQuizChange('grade_level', v)}
                  >
                    <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white" data-testid="quiz-grade-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {grades.map(g => (
                        <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Descrição</Label>
                  <Textarea
                    value={quizData.description}
                    onChange={(e) => handleQuizChange('description', e.target.value)}
                    placeholder="Descrição opcional..."
                    className="mt-1 bg-white/10 border-white/20 text-white resize-none"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Question List */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base">Questões</CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={addQuestion}
                    className="text-green-400 hover:text-green-300 h-8"
                    data-testid="add-question-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {questions.map((q, index) => (
                    <div
                      key={q.id}
                      onClick={() => setActiveQuestion(index)}
                      className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-2 group ${
                        activeQuestion === index
                          ? 'bg-purple-600/30 border border-purple-500'
                          : 'hover:bg-white/10'
                      }`}
                      data-testid={`question-item-${index}`}
                    >
                      <GripVertical className="w-4 h-4 text-gray-500" />
                      <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm text-white truncate flex-1">
                        {q.question || 'Nova questão'}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeQuestion(index);
                        }}
                        className="w-6 h-6 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Question Editor */}
          <div className="lg:col-span-3">
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="border-b border-white/10">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center font-medium">
                      {activeQuestion + 1}
                    </span>
                    Questão {activeQuestion + 1}
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <Select
                        value={String(currentQuestion.time_limit)}
                        onValueChange={(v) => handleQuestionChange(activeQuestion, 'time_limit', parseInt(v))}
                      >
                        <SelectTrigger className="w-24 h-8 bg-white/10 border-white/20 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10s</SelectItem>
                          <SelectItem value="20">20s</SelectItem>
                          <SelectItem value="30">30s</SelectItem>
                          <SelectItem value="45">45s</SelectItem>
                          <SelectItem value="60">60s</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-gray-400" />
                      <Select
                        value={currentQuestion.difficulty}
                        onValueChange={(v) => handleQuestionChange(activeQuestion, 'difficulty', v)}
                      >
                        <SelectTrigger className="w-24 h-8 bg-white/10 border-white/20 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {difficulties.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Question Text */}
                <div>
                  <Label className="text-gray-300">Enunciado da questão *</Label>
                  <Textarea
                    value={currentQuestion.question}
                    onChange={(e) => handleQuestionChange(activeQuestion, 'question', e.target.value)}
                    placeholder="Digite a pergunta aqui..."
                    className="mt-2 bg-white/10 border-white/20 text-white text-lg resize-none"
                    rows={3}
                    data-testid="question-text-input"
                  />
                </div>

                {/* Options */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-300">Alternativas *</Label>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => addOption(activeQuestion)}
                      className="text-green-400 hover:text-green-300 h-7 text-xs"
                      disabled={currentQuestion.options.length >= 6}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Adicionar opção
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {currentQuestion.options.map((option, optIndex) => (
                      <div
                        key={option.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          option.is_correct
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-white/20 bg-white/5'
                        }`}
                      >
                        <div
                          onClick={() => handleOptionChange(activeQuestion, optIndex, 'is_correct', true)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all ${
                            option.is_correct
                              ? 'bg-green-500 text-white'
                              : 'bg-white/20 text-gray-400 hover:bg-white/30'
                          }`}
                          data-testid={`option-correct-${optIndex}`}
                        >
                          {option.is_correct ? (
                            <Check className="w-5 h-5" />
                          ) : (
                            <span className="text-sm font-medium">
                              {String.fromCharCode(65 + optIndex)}
                            </span>
                          )}
                        </div>
                        
                        <Input
                          value={option.text}
                          onChange={(e) => handleOptionChange(activeQuestion, optIndex, 'text', e.target.value)}
                          placeholder={`Alternativa ${String.fromCharCode(65 + optIndex)}`}
                          className="flex-1 bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0"
                          data-testid={`option-text-${optIndex}`}
                        />
                        
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => removeOption(activeQuestion, optIndex)}
                          className="w-8 h-8 text-gray-400 hover:text-red-400"
                          disabled={currentQuestion.options.length <= 2}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-400 mt-2">
                    Clique no círculo para marcar a alternativa correta
                  </p>
                </div>

                {/* Explanation */}
                <div>
                  <Label className="text-gray-300">Explicação (opcional)</Label>
                  <Textarea
                    value={currentQuestion.explanation}
                    onChange={(e) => handleQuestionChange(activeQuestion, 'explanation', e.target.value)}
                    placeholder="Explique por que esta é a resposta correta..."
                    className="mt-2 bg-white/10 border-white/20 text-white resize-none"
                    rows={2}
                  />
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t border-white/10">
                  <Button
                    variant="outline"
                    onClick={() => setActiveQuestion(Math.max(0, activeQuestion - 1))}
                    disabled={activeQuestion === 0}
                    className="border-white/20 text-white"
                  >
                    Anterior
                  </Button>
                  
                  {activeQuestion === questions.length - 1 ? (
                    <Button
                      onClick={addQuestion}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nova Questão
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setActiveQuestion(Math.min(questions.length - 1, activeQuestion + 1))}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Próxima
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
